import { gql } from '@urql/core';

/**
 * Category operations (CATEGORY-SPLIT sprint, 2026-04-23).
 *
 * Categories are a first-class entity, separate from commerce kernel Collections.
 * These ops target the new `categories` / `category` / `createCategory` etc.
 * admin API surface defined in @tali/catalog/category.
 */

// ─────────────────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────────────────

export const GET_CATEGORIES = gql`
  query GetCategories($options: CategoryListOptions) {
    categories(options: $options) {
      items {
        id
        name
        slug
        description
        isPrivate
        position
        parentId
        parent {
          id
          name
        }
        children {
          id
        }
        breadcrumbs {
          id
          name
          slug
        }
        featuredAsset {
          id
          preview
        }
        coverAsset {
          id
          preview
        }
        productCount
        translations {
          id
          languageCode
          name
          slug
          description
          metaTitle
          metaDescription
        }
      }
      totalItems
    }
  }
`;

export const GET_CATEGORY = gql`
  query GetCategory($id: ID!) {
    category(id: $id) {
      id
      name
      slug
      description
      isPrivate
      position
      parentId
      parent {
        id
        name
      }
      children {
        id
        name
        slug
      }
      breadcrumbs {
        id
        name
        slug
      }
      featuredAsset {
        id
        preview
      }
      coverAsset {
        id
        preview
      }
      productCount
      translations {
        id
        languageCode
        name
        slug
        description
        metaTitle
        metaDescription
      }
    }
  }
`;

export const GET_CATEGORY_TREE = gql`
  query GetCategoryTree {
    categoryTree {
      id
      name
      slug
      position
      isPrivate
      children {
        id
        name
        slug
        position
      }
      featuredAsset {
        id
        preview
      }
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────────────────

export const CREATE_CATEGORY = gql`
  mutation CreateCategory($input: CreateCategoryInput!) {
    createCategory(input: $input) {
      id
      name
      slug
      translations {
        id
        languageCode
        name
        slug
      }
    }
  }
`;

export const UPDATE_CATEGORY = gql`
  mutation UpdateCategory($input: UpdateCategoryInput!) {
    updateCategory(input: $input) {
      id
      name
      slug
      translations {
        id
        languageCode
        name
        slug
      }
    }
  }
`;

export const DELETE_CATEGORY = gql`
  mutation DeleteCategory($id: ID!, $cascade: Boolean) {
    deleteCategory(id: $id, cascade: $cascade) {
      result
      message
    }
  }
`;

export const MOVE_CATEGORY = gql`
  mutation MoveCategory($id: ID!, $newParentId: ID) {
    moveCategory(id: $id, newParentId: $newParentId) {
      id
      parentId
    }
  }
`;

export const ASSIGN_PRODUCTS_TO_CATEGORY = gql`
  mutation AssignProductsToCategory($categoryId: ID!, $productIds: [ID!]!) {
    assignProductsToCategory(categoryId: $categoryId, productIds: $productIds)
  }
`;

export const REMOVE_PRODUCTS_FROM_CATEGORY = gql`
  mutation RemoveProductsFromCategory($categoryId: ID!, $productIds: [ID!]!) {
    removeProductsFromCategory(
      categoryId: $categoryId
      productIds: $productIds
    )
  }
`;

export const SET_PRODUCT_CATEGORIES = gql`
  mutation SetProductCategories($input: SetProductCategoriesInput!) {
    setProductCategories(input: $input) {
      success
    }
  }
`;

export const SET_PRIMARY_CATEGORY = gql`
  mutation SetPrimaryCategory($productId: ID!, $categoryId: ID!) {
    setPrimaryCategory(productId: $productId, categoryId: $categoryId)
  }
`;

export const REORDER_PRODUCTS_IN_CATEGORY = gql`
  mutation ReorderProductsInCategory($categoryId: ID!, $orderedProductIds: [ID!]!) {
    reorderProductsInCategory(
      categoryId: $categoryId
      orderedProductIds: $orderedProductIds
    )
  }
`;

export const SET_PRODUCT_CATEGORY_PINNING = gql`
  mutation SetProductCategoryPinning($input: SetProductCategoryPinningInput!) {
    setProductCategoryPinning(input: $input) {
      productId
      categoryId
      isPinned
      pinPosition
    }
  }
`;
